import { useAuth } from "@elit-hair/elit-tools";
import { Button, DateRangePicker, EmptyState, ErrorState, Icon, PaginationControls } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { TbAlertTriangle, TbExternalLink } from "react-icons/tb";
import { toast } from "sonner";
import z from "zod";

import { PageActions } from "@/components/page-actions";
import { PageFiltersDialog } from "@/components/page-filters-dialog";
import { PAGINATION } from "@/constants/pagination";
import { useUsersByIdsBatch } from "@/hooks/queries/user.queries";
import { useGetIncidents, useUpdateIncident } from "@/queries/incidents.queries";
import {
    INCIDENT_REPORT_PRIORITY,
    INCIDENT_REPORT_STATUS,
    type IncidentReport,
    type IncidentReportFilters,
} from "@/types/incident.types";
import type { PageFilterSummaryItem } from "@/types/page-filters.types";
import { createPaginationProps } from "@/types/pagination.types";
import { convertFromDateRange } from "@/utils/format-date";
import { APP_ROUTES_FULL } from "@/utils/routes";
import { searchValidator } from "@/utils/search-validator";

import { IncidentDetailSheet } from "./components/incident-detail-sheet";
import { IncidentReviewersList, IncidentReviewersListVariant } from "./components/incident-reviewers-list";
import { IncidentStatusBadge, getIncidentStatusTranslationKey } from "./components/incident-status-badge";
import { IncidentsFilters } from "./components/incidents-filters";
import { IncidentsTableSkeleton } from "./components/incidents-table-skeleton";
import { ReportIncidentDialog } from "./components/report-incident-dialog";

const IncidentsSearchParamsSchema = z.object({
    incidentId: z.string().optional(),
    assignToMe: z.string().optional(),
});

export const Route = createFileRoute(APP_ROUTES_FULL.INCIDENTS)({
    component: Incidents,
    validateSearch: searchValidator(IncidentsSearchParamsSchema),
});

const PRIORITY_BADGE_VARIANT: Record<INCIDENT_REPORT_PRIORITY, string> = {
    [INCIDENT_REPORT_PRIORITY.LOW]: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
    [INCIDENT_REPORT_PRIORITY.MEDIUM]: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400",
    [INCIDENT_REPORT_PRIORITY.HIGH]: "bg-red-500/15 text-red-700 dark:text-red-400",
};

function Incidents() {
    const { userData } = useAuth();
    const { t } = useTranslation();
    const search = Route.useSearch({
        structuralSharing: true,
    });
    const navigate = Route.useNavigate();
    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const updateIncidentMutation = useUpdateIncident();
    const [hasAutoAssigned, setHasAutoAssigned] = useState(false);

    const [statusFilter, setStatusFilter] = useState<INCIDENT_REPORT_STATUS | "">("");
    const [priorityFilter, setPriorityFilter] = useState<INCIDENT_REPORT_PRIORITY | "">("");
    const [typeFilter, setTypeFilter] = useState("");
    const [reporterFilter, setReporterFilter] = useState<string | undefined>(undefined);
    const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
    const [page, setPage] = useState<number>(PAGINATION.DEFAULT_PAGE);

    const { startDate, endDate } = useMemo(() => convertFromDateRange(dateRange), [dateRange]);

    const filters = useMemo<IncidentReportFilters>(() => {
        const f: IncidentReportFilters = {
            page,
            limit: PAGINATION.PAGE_SIZE,
        };
        if (statusFilter) f.status = statusFilter;
        if (priorityFilter) f.priority = priorityFilter;
        if (typeFilter.trim()) f.type = typeFilter.trim();
        if (reporterFilter) f.userId = reporterFilter;
        if (startDate) f.start = startDate;
        if (endDate) f.end = endDate;
        return f;
    }, [statusFilter, priorityFilter, typeFilter, reporterFilter, startDate, endDate, page]);

    useEffect(() => {
        setPage(PAGINATION.DEFAULT_PAGE);
    }, [statusFilter, priorityFilter, typeFilter, reporterFilter, startDate, endDate]);

    const { data, isLoading, isError } = useGetIncidents(filters);
    const incidents = data?.data ?? [];

    const paginationProps = createPaginationProps({
        response: data,
        onPageChange: setPage,
        isLoading,
    });

    const uniqueUserIds = useMemo(
        () =>
            Array.from(
                new Set(
                    incidents.flatMap((incident) => [incident.userId, ...(incident.reviewers ?? [])]).filter(Boolean)
                )
            ),
        [incidents]
    );
    const { data: batchUsers = [] } = useUsersByIdsBatch(uniqueUserIds, uniqueUserIds.length > 0);
    const usersMap = useMemo(() => Object.fromEntries(batchUsers.map((u) => [u.id, u])), [batchUsers]);
    const selectedIncident = useMemo<IncidentReport | null>(() => {
        if (!search.incidentId) return null;
        return incidents.find((incident) => incident.id === search.incidentId) ?? null;
    }, [incidents, search.incidentId]);

    const assignToMeRequested = search.assignToMe === "1" || search.assignToMe === "true";

    useEffect(() => {
        if (!assignToMeRequested) return;
        if (!search.incidentId) return;
        if (hasAutoAssigned) return;

        setHasAutoAssigned(true);

        updateIncidentMutation
            .mutateAsync({ id: search.incidentId, status: INCIDENT_REPORT_STATUS.ASSIGNED })
            .then(() => {
                navigate({
                    to: Route.to,
                    search: (old) => {
                        const updatedSearchParams = { ...old };
                        delete updatedSearchParams.assignToMe;
                        return updatedSearchParams;
                    },
                    replace: true,
                });
            })
            .catch((e) => {
                setHasAutoAssigned(false);
                const message = e instanceof Error ? e.message : "";
                toast.error(message || t("error"));
            });
    }, [assignToMeRequested, hasAutoAssigned, navigate, search.incidentId, t, updateIncidentMutation]);

    const hasActiveFilters = !!(statusFilter || priorityFilter || typeFilter || reporterFilter);

    const clearFilters = () => {
        setStatusFilter("");
        setPriorityFilter("");
        setTypeFilter("");
        setReporterFilter(undefined);
        setPage(PAGINATION.DEFAULT_PAGE);
    };

    const filterSummary = useMemo<PageFilterSummaryItem[]>(() => {
        const items: PageFilterSummaryItem[] = [];

        if (statusFilter) {
            items.push({ id: "status", label: t(getIncidentStatusTranslationKey(statusFilter)) });
        }

        if (priorityFilter) {
            items.push({ id: "priority", label: t(`incident_priority_${priorityFilter.toLowerCase()}`) });
        }

        if (reporterFilter) {
            items.push({
                id: "reporter",
                label: reporterFilter === userData?.id ? t("incident_filter_me") : t("incident_filter_reporter"),
            });
        }

        return items;
    }, [priorityFilter, reporterFilter, statusFilter, t, userData?.id]);

    return (
        <div className="flex h-full flex-col gap-4">
            <PageFiltersDialog summary={filterSummary}>
                <IncidentsFilters
                    statusFilter={statusFilter}
                    onStatusFilterChange={setStatusFilter}
                    priorityFilter={priorityFilter}
                    onPriorityFilterChange={setPriorityFilter}
                    reporterFilter={reporterFilter}
                    onReporterFilterChange={setReporterFilter}
                    currentUserId={userData?.id}
                    hasActiveFilters={hasActiveFilters}
                    onClearFilters={clearFilters}
                />
            </PageFiltersDialog>
            <PageActions>
                <DateRangePicker
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                    showClearButton
                    showTodayButton
                    buttonWidth="w-56"
                    placeholder={t("incident_filter_date_range")}
                />
                <Button
                    onClick={() => setReportDialogOpen(true)}
                    className="gap-2 bg-amber-600 text-white shadow-md transition-all hover:bg-amber-700 hover:shadow-lg dark:bg-amber-500 dark:hover:bg-amber-600"
                >
                    <Icon icon={TbAlertTriangle} />
                    {t("report_incident")}
                </Button>
            </PageActions>

            {/* Table */}
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border">
                <div className="min-h-0 flex-1 overflow-auto">
                    {isError ? (
                        <div className="flex h-full items-center justify-center py-16">
                            <ErrorState title={t("incidents_loading_error")} />
                        </div>
                    ) : (
                        <table className="w-full caption-bottom border-collapse text-sm">
                            <thead className="sticky top-0 z-10 bg-background">
                                <tr className="border-b border-border">
                                    <th className="h-10 w-12 px-3 text-center align-middle text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                        #
                                    </th>
                                    <th className="h-10 px-3 text-left align-middle text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                        {t("incident_description")}
                                    </th>
                                    <th className="h-10 w-28 px-3 text-left align-middle text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                        {t("incident_status")}
                                    </th>
                                    <th className="h-10 w-28 px-3 text-left align-middle text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                        {t("incident_priority")}
                                    </th>
                                    <th className="h-10 w-32 px-3 text-left align-middle text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                        {t("type")}
                                    </th>
                                    <th className="h-10 px-3 text-left align-middle text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                        {t("incident_user")}
                                    </th>
                                    <th className="h-10 px-3 text-left align-middle text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                        {t("incident_reviewers")}
                                    </th>
                                    <th className="h-10 px-3 text-left align-middle text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                        {t("incident_page")}
                                    </th>
                                    <th className="h-10 w-44 px-3 text-center align-middle text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                        {t("incident_timestamp")}
                                    </th>
                                    <th className="h-10 w-14 px-3" />
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <IncidentsTableSkeleton />
                                ) : incidents.length === 0 ? (
                                    <tr>
                                        <td colSpan={10} className="py-16">
                                            <EmptyState variant="simple" title="incidents_no_data" />
                                        </td>
                                    </tr>
                                ) : (
                                    incidents.map((incident, index) => {
                                        const user = usersMap[incident.userId];
                                        const createdAt = DateTime.fromISO(incident.createdAt);

                                        return (
                                            <tr
                                                key={incident.id}
                                                className="border-b border-border last:border-0 hover:bg-muted/40"
                                            >
                                                <td className="px-3 py-3 text-center text-muted-foreground">
                                                    {(paginationProps.page - 1) * paginationProps.pageSize + index + 1}
                                                </td>
                                                <td className="max-w-xs px-3 py-3">
                                                    <p className="line-clamp-2 text-sm">{incident.description}</p>
                                                </td>
                                                <td className="px-3 py-3">
                                                    <IncidentStatusBadge status={incident.status} />
                                                </td>
                                                <td className="px-3 py-3">
                                                    <span
                                                        className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${PRIORITY_BADGE_VARIANT[incident.priority]}`}
                                                    >
                                                        {t(`incident_priority_${incident.priority.toLowerCase()}`)}
                                                    </span>
                                                </td>
                                                <td className="max-w-[180px] truncate px-3 py-3 text-sm text-muted-foreground">
                                                    {incident.type?.trim() || "—"}
                                                </td>
                                                <td className="px-3 py-3 text-sm text-muted-foreground">
                                                    {user ? (
                                                        <span title={user.email}>{user.name}</span>
                                                    ) : (
                                                        <span className="font-mono text-xs">{incident.userId}</span>
                                                    )}
                                                </td>
                                                <td className="max-w-[200px] px-3 py-3">
                                                    <IncidentReviewersList
                                                        reviewerIds={incident.reviewers ?? []}
                                                        usersMap={usersMap}
                                                        variant={IncidentReviewersListVariant.DETAILED}
                                                    />
                                                </td>
                                                <td className="max-w-40 truncate px-3 py-3 text-sm text-muted-foreground">
                                                    {incident.metadata?.page ?? "—"}
                                                </td>
                                                <td className="px-3 py-3 text-center text-xs text-muted-foreground">
                                                    <span title={createdAt.toISO() ?? ""}>
                                                        {createdAt.toLocaleString(DateTime.DATETIME_SHORT)}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-3 text-center">
                                                    <button
                                                        onClick={() =>
                                                            navigate({
                                                                to: Route.to,
                                                                search: (old) => ({ ...old, incidentId: incident.id }),
                                                            })
                                                        }
                                                        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                                        title={t("incident_view_detail")}
                                                    >
                                                        <Icon icon={TbExternalLink} />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
                {!isLoading && !isError && incidents.length > 0 && (
                    <div className="flex shrink-0 flex-col items-center justify-between gap-4 border-t border-border bg-muted/30 px-4 py-3 sm:flex-row">
                        <p className="text-sm text-muted-foreground">
                            {t("showing_results", {
                                start: (paginationProps.page - 1) * paginationProps.pageSize + 1,
                                end: Math.min(
                                    paginationProps.page * paginationProps.pageSize,
                                    paginationProps.total || incidents.length
                                ),
                                total: paginationProps.total || incidents.length,
                            })}
                        </p>
                        <PaginationControls
                            currentPage={paginationProps.page}
                            totalPages={Math.max(paginationProps.totalPages, 1)}
                            onPageChange={paginationProps.onPageChange!}
                            isLoading={paginationProps.isLoading}
                            className="shrink-0"
                        />
                    </div>
                )}
            </div>

            <ReportIncidentDialog open={reportDialogOpen} onOpenChange={setReportDialogOpen} />

            <IncidentDetailSheet
                incident={selectedIncident}
                onOpenChange={(open) => {
                    if (open) return;
                    navigate({
                        to: Route.to,
                        search: (old) => {
                            const updatedSearchParams = { ...old };
                            delete updatedSearchParams.incidentId;
                            return updatedSearchParams;
                        },
                        replace: true,
                    });
                }}
            />
        </div>
    );
}
